import { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';

const CORES_LINHA = ['#3b82f6', '#a78bfa', '#34d399', '#fbbf24', '#f472b6', '#60a5fa', '#fb923c'];

function TooltipPersonalizado({ active, payload, label, unidade }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-400 font-medium mb-1.5">{label}</p>
      {payload.map((entry) =>
        entry.value != null ? (
          <div key={entry.dataKey} className="flex items-center gap-2 mb-0.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-400">Ponto {entry.dataKey} {unidade}:</span>
            <span className="font-mono font-semibold text-slate-200">{entry.value.toFixed(4)}</span>
          </div>
        ) : null
      )}
    </div>
  );
}

export default function TendenciaErro({ instrumentId }) {
  const [calibracoes, setCalibracoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const buscarCalibracoes = useCallback(async () => {
    if (!instrumentId) return;
    setCarregando(true);
    setErro('');
    try {
      const { data } = await api.get(`/calibrations?instrumentId=${instrumentId}&limite=50`);
      const lista = data.dados || data;
      // Ordena da mais antiga para a mais recente para o eixo X progredir corretamente
      setCalibracoes([...lista].sort((a, b) => new Date(a.data) - new Date(b.data)));
    } catch {
      setErro('Não foi possível carregar os dados de tendência.');
    } finally {
      setCarregando(false);
    }
  }, [instrumentId]);

  useEffect(() => {
    buscarCalibracoes();
  }, [buscarCalibracoes]);

  if (carregando) {
    return (
      <div className="space-y-4 mt-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-64 bg-[var(--surface-4)] rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (erro) {
    return (
      <div className="mt-4 bg-red-900/30 border border-red-800/50 text-red-300 rounded-lg px-4 py-3 text-sm">
        {erro}
      </div>
    );
  }

  const calibracoesComMedicoes = calibracoes.filter(
    (c) => c.gruposMedicao?.some((g) => g.pontos?.length > 0)
  );

  if (calibracoesComMedicoes.length < 2) {
    return (
      <div className="mt-4 flex flex-col items-center justify-center py-12 text-slate-500">
        <span className="text-4xl mb-3">📈</span>
        <p className="text-sm font-medium text-slate-400">Dados insuficientes para tendência</p>
        <p className="text-xs mt-1 text-center max-w-xs">
          São necessárias ao menos 2 calibrações com medições registradas para exibir o gráfico de tendência de erro.
        </p>
      </div>
    );
  }

  // Constrói mapa de grandezas a partir das calibrações com medições
  const grandezaMap = {};
  const datasOrdenadas = [];

  calibracoesComMedicoes.forEach((cal) => {
    const dateStr = new Date(cal.data).toLocaleDateString('pt-BR');
    if (!datasOrdenadas.includes(dateStr)) datasOrdenadas.push(dateStr);

    cal.gruposMedicao?.forEach((grupo) => {
      if (!grupo.pontos?.length) return;

      if (!grandezaMap[grupo.nome]) {
        grandezaMap[grupo.nome] = {
          unidade: grupo.unidade,
          empTipo: grupo.empTipo,
          empValor: grupo.empValor,
          aplicadoValues: new Set(),
          pontosPorData: {},
        };
      }

      const g = grandezaMap[grupo.nome];
      // Sempre atualiza EMP com o valor mais recente (calibrações estão ordenadas ASC)
      g.empValor = grupo.empValor;
      g.empTipo = grupo.empTipo;

      if (!g.pontosPorData[dateStr]) g.pontosPorData[dateStr] = {};

      grupo.pontos.forEach((ponto) => {
        const chave = String(ponto.aplicado);
        g.aplicadoValues.add(chave);
        if (ponto.erro != null) {
          g.pontosPorData[dateStr][chave] = ponto.erro;
        }
      });
    });
  });

  // Transforma para formato Recharts
  const grandezas = Object.entries(grandezaMap).map(([nome, info]) => {
    const aplicados = Array.from(info.aplicadoValues).sort(
      (a, b) => parseFloat(a) - parseFloat(b)
    );

    const chartData = datasOrdenadas.map((dateStr) => {
      const ponto = { data: dateStr };
      aplicados.forEach((ap) => {
        ponto[ap] = info.pontosPorData[dateStr]?.[ap] ?? null;
      });
      return ponto;
    });

    const emp = parseFloat(info.empValor);
    const temEmpNumerico = !isNaN(emp) && info.empTipo !== 'text';

    return { nome, ...info, aplicados, chartData, emp, temEmpNumerico };
  });

  return (
    <div className="space-y-6 mt-4">
      {grandezas.map((g, gi) => (
        <div key={gi} className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-4">
          {/* Cabeçalho do card */}
          <div className="flex items-center gap-3 mb-1">
            <h4 className="text-sm font-semibold text-slate-200">{g.nome}</h4>
            <span className="text-xs text-slate-500">({g.unidade})</span>
            <div className="ml-auto flex items-center gap-2">
              {g.temEmpNumerico && (
                <span className="text-xs bg-red-950/60 text-red-400 border border-red-900/50 px-2 py-0.5 rounded font-mono">
                  EMP ±{g.empValor} {g.empTipo === 'percentage' ? '%' : g.unidade}
                </span>
              )}
              <span className="text-xs text-slate-600">
                {datasOrdenadas.length} calibrações · {g.aplicados.length} pontos
              </span>
            </div>
          </div>

          <p className="text-[10px] text-slate-600 mb-4">
            Erro = valor lido − valor aplicado. Linhas tracejadas vermelhas = limite EMP.
          </p>

          <ResponsiveContainer width="100%" height={230}>
            <LineChart
              data={g.chartData}
              margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--surface-4)" vertical={false} />
              <XAxis
                dataKey="data"
                tick={{ fill: '#64748b', fontSize: 10 }}
                axisLine={{ stroke: 'var(--surface-4)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => v.toFixed(3)}
                width={52}
              />
              <Tooltip content={<TooltipPersonalizado unidade={g.unidade} />} />
              <Legend
                formatter={(value) => (
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>
                    Ponto {value} {g.unidade}
                  </span>
                )}
              />

              {/* Linha do zero */}
              <ReferenceLine y={0} stroke="#334155" strokeWidth={1} />

              {/* Linhas EMP */}
              {g.temEmpNumerico && (
                <>
                  <ReferenceLine
                    y={g.emp}
                    stroke="#f87171"
                    strokeDasharray="5 3"
                    strokeWidth={1}
                    label={{ value: `+${g.emp}`, fill: '#f87171', fontSize: 9, position: 'insideTopRight' }}
                  />
                  <ReferenceLine
                    y={-g.emp}
                    stroke="#f87171"
                    strokeDasharray="5 3"
                    strokeWidth={1}
                    label={{ value: `-${g.emp}`, fill: '#f87171', fontSize: 9, position: 'insideBottomRight' }}
                  />
                </>
              )}

              {/* Uma linha por ponto de calibração */}
              {g.aplicados.map((ap, idx) => (
                <Line
                  key={ap}
                  type="monotone"
                  dataKey={ap}
                  stroke={CORES_LINHA[idx % CORES_LINHA.length]}
                  strokeWidth={1.5}
                  dot={{ r: 3, fill: CORES_LINHA[idx % CORES_LINHA.length], strokeWidth: 0 }}
                  activeDot={{ r: 5, strokeWidth: 0 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ))}
    </div>
  );
}
